import { Injectable, inject } from '@angular/core';
import { Store } from '@ngxs/store';
import { IsOneActivityStartedPipe } from '@wilson/activities/pipes';
import { AuthState } from '@wilson/auth/core';
import { Shift, ShiftState } from '@wilson/interfaces';
import { ResolvedShiftsState } from '@wilson/shifts-mobile';
import {
  endOfMonth,
  isAfter,
  isBefore,
  startOfMonth,
  subMonths,
} from 'date-fns';
import { combineLatest, map } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class HomeStateService {
  private readonly isOneActivityStartedPipe = inject(IsOneActivityStartedPipe);
  private readonly store = inject(Store);

  getShifts() {
    const notSubmitted$ = this.store
      .select(ResolvedShiftsState.shiftsInDateRangeSelector)
      .pipe(
        map((shiftsInDateRangeFn) => {
          const startDate = startOfMonth(subMonths(new Date(), 1));
          const endDate = endOfMonth(new Date());
          return shiftsInDateRangeFn(
            startDate,
            endDate,
            this.store.selectSnapshot(AuthState.userId)!,
          ).filter(this.notSubmittedFilterFn);
        }),
      );

    // Reopened shifts are read from the full state (no date window) because
    // an admin can send back a shift from any point in the past.
    // HydrateReopenedShifts ensures these are loaded into state on home page init.
    const reopened$ = this.store.select(ResolvedShiftsState.shifts).pipe(
      map((shifts) => {
        const userId = this.store.selectSnapshot(AuthState.userId);
        return shifts
          .filter(
            (s) =>
              !s.delete &&
              s.userId === userId &&
              s.state === ShiftState.Reopened,
          )
          .map((s) => ({
            ...s,
            activities: s.activities?.filter((a) => !a.deletedAt) ?? [],
          }));
      }),
    );

    return combineLatest([notSubmitted$, reopened$]).pipe(
      map(([notSubmitted, reopened]) =>
        [...notSubmitted, ...reopened].sort(this.shiftsSortFn),
      ),
    );
  }

  // Keeps the date-range window (prev month → end of current month) intentionally:
  // unsubmitted shifts older than that window are not actionable from the home page.
  private notSubmittedFilterFn = (shift: Shift) =>
    shift.state === ShiftState.NotSubmitted && this.isActionableFn(shift);

  // A shift is actionable once it has ended and none of its activities is running.
  private isActionableFn = (shift: Shift) => {
    const isFutureShift = isBefore(
      new Date(),
      new Date(shift.activities![shift.activities!.length - 1].endDatetime),
    );
    return (
      !isFutureShift &&
      !this.isOneActivityStartedPipe.transform(shift.activities)
    );
  };

  private shiftsSortFn = (shiftA: Shift, shiftB: Shift) => {
    return isAfter(new Date(shiftB.startDate), new Date(shiftA.startDate))
      ? 1
      : -1;
  };
}
